import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar se usuário está logado ao carregar a aplicação
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = Cookies.get('token');
        if (token) {
          const response = await authService.verifyToken();
          if (response.success) {
            setUser(response.user);
            setIsAuthenticated(true);
          } else {
            // Token inválido, remover
            Cookies.remove('token');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        Cookies.remove('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authService.login(email, password);
      
      if (response.success) {
        // Salvar token no cookie
        Cookies.set('token', response.token, { expires: 1 }); // 1 dia
        
        setUser(response.user);
        setIsAuthenticated(true);
        
        toast.success('Login realizado com sucesso!');
        return { success: true };
      } else {
        toast.error(response.message || 'Erro ao fazer login');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = () => {
    Cookies.remove('token');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logout realizado com sucesso!');
  };

  // Registrar usuário
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authService.register(userData);
      
      if (response.success) {
        toast.success('Usuário registrado com sucesso!');
        return { success: true };
      } else {
        toast.error(response.message || 'Erro ao registrar usuário');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erro no registro:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Esqueci minha senha
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      const response = await authService.forgotPassword(email);
      
      if (response.success) {
        toast.success('Email de recuperação enviado!');
        return { success: true };
      } else {
        toast.error(response.message || 'Erro ao enviar email');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Reset de senha
  const resetPassword = async (token, newPassword) => {
    try {
      setLoading(true);
      const response = await authService.resetPassword(token, newPassword);
      
      if (response.success) {
        toast.success('Senha alterada com sucesso!');
        return { success: true };
      } else {
        toast.error(response.message || 'Erro ao alterar senha');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      const message = error.response?.data?.message || 'Erro interno do servidor';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Atualizar dados do usuário
  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};